/**
 * bridge/contact_picker_controller.js — "contact-picker" bridge component
 * (Stimulus + Strada).
 *
 * Custom-tier bridge component: open the device contact picker and return a
 * contact's { name, phone, email, address } so a tradie can create a Chippie
 * client from a saved contact. Stimulus identifier "bridge--contact-picker"
 * (glob-derived from this subdir); Strada component name "contact-picker" MUST
 * match the iOS class var name
 * (native/ios/Chippie/Bridge/ContactPickerComponent.swift).
 *
 * Why it extends Strada's BridgeComponent: the iOS request is reached via
 * this.send("open", {}, callback), which only works on a connected bridge
 * component inside the iOS Hotwire Native shell. Extending BridgeComponent gives
 * us that send() transport AND the UA-gated connect() (it only connects in a
 * native shell).
 *
 * Three-way transport: rather than each caller branching on platform, this
 * controller registers a single contactPicker() impl set onto
 * window.ChippieBridge ({ ios, android, pwa }). The facade (chippie-bridge.js)
 * owns platform detection and picks the right impl per call. Each impl is async
 * and resolves to { name, phone, email, address } (or rejects on cancel /
 * unavailable).
 *   - ios:     this.send("open", {}, msg) over Strada to
 *              ContactPickerComponent.swift
 *   - android: window.ChippieNative.pickContact() (binder thread); the native
 *              side hands the result back by invoking the global callback
 *              window.__chippieContactResult(json) — set up here per request
 *              (one in-flight request at a time, mirroring iOS).
 *   - pwa:     the experimental Contact Picker API (navigator.contacts.select),
 *              available on Android Chrome PWAs; rejects where unsupported
 *              (desktop, iOS Safari) so the caller can fall back to manual entry.
 *
 * Callers use window.ChippieBridge.contactPicker() and await the contact, OR
 * wire a button with data-action="bridge--contact-picker#open" and listen for
 * the dispatched Stimulus events on this controller's element:
 *   - bridge--contact-picker:picked  detail: { name, phone, email, address }
 *   - bridge--contact-picker:error
 *
 * Template wiring:
 *   <div data-controller="bridge--contact-picker"
 *        data-action="bridge--contact-picker:picked->client-form#fill">
 *     <button data-action="bridge--contact-picker#open">From contacts</button>
 *   </div>
 *
 * The Android + PWA impls carry no per-instance state, so they are registered at
 * module load via registerStandalone() and ChippieBridge.contactPicker() resolves
 * everywhere. Only the ios impl needs a live Strada instance for this.send, so
 * connect() overrides it and disconnect() reverts to the rejecting placeholder.
 * Registering solely on connect (as this file used to) left
 * ChippieBridge.contactPicker UNDEFINED on PWA and desktop, where the UA-gated
 * BridgeComponent.connect() never fires — callers got a TypeError, not a
 * rejection they could fall back from.
 *
 * No canonical Joe Masilotti contact component exists; the web shape follows the
 * sibling location_controller.js (ChippieBridge facade registration + Android
 * transport + PWA fallback).
 */

import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// Global name the Android native side calls to deliver a result. Kept in sync
// with the Kotlin handler (ChippieNativeBridge.pickContact).
const ANDROID_RESULT_CALLBACK = "__chippieContactResult";

// Shape every transport resolves to, so callers get a stable object even when a
// field is missing on the source contact.
export function normalize(data) {
  const c = data || {};
  return {
    name: c.name || "",
    phone: c.phone || "",
    email: c.email || "",
    address: c.address || "",
  };
}

// Android: call the @JavascriptInterface, which opens the picker off the binder
// thread and invokes the global result callback with a JSON string (or null on
// cancel). One request in flight.
function androidPick() {
  return new Promise((resolve, reject) => {
    window[ANDROID_RESULT_CALLBACK] = (json) => {
      delete window[ANDROID_RESULT_CALLBACK];
      if (!json) {
        reject(new Error("contact-cancelled"));
        return;
      }
      try {
        resolve(normalize(JSON.parse(json)));
      } catch (_e) {
        reject(new Error("contact-unavailable"));
      }
    };
    try {
      window.ChippieNative.pickContact();
    } catch (_e) {
      delete window[ANDROID_RESULT_CALLBACK];
      reject(new Error("contact-unavailable"));
    }
  });
}

// PWA: the experimental Contact Picker API (Android Chrome). Requests a single
// contact's name/phone/email/address; rejects where the API is absent so the
// caller falls back to manual entry.
async function pwaPick() {
  const picker = navigator.contacts;
  if (!picker || typeof picker.select !== "function") {
    throw new Error("contact-unavailable");
  }
  const props = ["name", "tel", "email", "address"];
  const supported =
    typeof picker.getProperties === "function"
      ? await picker.getProperties()
      : props;
  const want = props.filter((p) => supported.includes(p));
  const results = await picker.select(want, { multiple: false });
  if (!results || results.length === 0) {
    throw new Error("contact-cancelled");
  }
  return normalize(flattenWebContact(results[0]));
}

function registerStandalone() {
  if (!window.ChippieBridge || !window.ChippieBridge.register) return;
  window.ChippieBridge.register("contactPicker", {
    // ios is overridden by the controller's connect() with a real send; this
    // placeholder rejects rather than hanging if somehow selected before connect.
    ios: () => Promise.reject(new Error("contact-unavailable")),
    android: androidPick,
    pwa: pwaPick,
  });
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
  registerStandalone();
} else {
  document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
  static component = "contact-picker";

  connect() {
    super.connect();
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("contactPicker", {
      ios: () =>
        new Promise((resolve, reject) => {
          this.send("open", {}, (message) => {
            const data = message.data || {};
            // Empty reply (user cancelled) => no name/phone/email/address.
            if (!data.name && !data.phone && !data.email && !data.address) {
              reject(new Error("contact-cancelled"));
            } else {
              resolve(normalize(data));
            }
          });
        }),
      android: androidPick,
      pwa: pwaPick,
    });
  }

  disconnect() {
    // Revert the live-send ios impl to the rejecting placeholder instead of
    // deleting the key — deleting also took the capability away from Android and
    // PWA, which never needed this instance.
    registerStandalone();
    super.disconnect();
  }

  // data-action="bridge--contact-picker#open" trigger — pick a contact, then
  // dispatch a Stimulus event other controllers/forms can react to.
  open(event) {
    if (event) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
    window.ChippieBridge.contactPicker().then(
      (contact) => {
        this.dispatch("picked", { detail: contact });
      },
      () => {
        this.dispatch("error");
      }
    );
  }
}

// The Contact Picker API returns array-valued fields and a structured address;
// flatten to the first value of each, matching the native shape.
export function flattenWebContact(contact) {
  const first = (arr) => (Array.isArray(arr) && arr.length ? arr[0] : "");
  const addr = first(contact.address);
  let address = "";
  if (addr) {
    address = [
      (addr.addressLine || []).join(" "),
      addr.city,
      addr.region,
      addr.postalCode,
      addr.country,
    ]
      .filter(Boolean)
      .join(", ");
  }
  return {
    name: first(contact.name),
    phone: first(contact.tel),
    email: first(contact.email),
    address,
  };
}
